import { createSelector as createReduxSelector } from 'reselect';
import type { IRootDispatch, RootState } from './models';
import stateManager from '../modules/user/stateManager';
import {
  calculatePercentComplete,
  fetchProviderOnboardingPromptStatus,
  ProviderOnboardingPromptStatus,
  ProviderOnboardingStatus,
} from '../modules/api/providers/onboarding';

export type StatusState = {
  providerId: number;
  isFetching: boolean;
  status?: ProviderOnboardingPromptStatus;
};

type State = {} | StatusState;

function isStatusState(state: any): state is StatusState {
  return typeof state === 'object' && state.providerId !== undefined;
}

function getSkipActionName(step: string): string {
  // We changed the step names, but want to retain skip state so have to do a little funky
  // string manipulation here:
  const part = step.replace(/^prompt_for_/, 'has_');
  return `proOnboardingStatusStream:skip:${part}`;
}

/**
 * Transform fetched data by checking for skipped or invalid steps
 * @param steps - fetched onboarding prompt status data
 * @returns {Promise<{}>}
 */
async function transformPromptStatus(
  steps: ProviderOnboardingPromptStatus,
): Promise<ProviderOnboardingPromptStatus> {
  const onboardingSteps: ProviderOnboardingPromptStatus = { ...steps };

  const stepsToCheck = [];
  Object.keys(steps).forEach(step => {
    stepsToCheck.push(getSkipActionName(step));
  });
  const stepsSkipped = await stateManager.getActionValues(...stepsToCheck);
  Object.keys(steps).forEach(step => {
    if (stepsSkipped[getSkipActionName(step)]) {
      onboardingSteps[step] = false;
    }
  });

  return onboardingSteps;
}

type StatusSelectorProps = {
  providerId: number;
  excludeSubscribeStep: boolean;
};

export const selectors = {
  status:
    createReduxSelector<
    RootState, StatusSelectorProps, // homogeneous selector parameters
    RootState['providerOnboarding'], number, boolean, // selector results
    ProviderOnboardingStatus | undefined // combiner result
    >(
      rootState => rootState.providerOnboarding,
      (rootState, { providerId }) => providerId,
      (rootState, { excludeSubscribeStep }) => excludeSubscribeStep,
      (state, providerId, excludeSubscribeStep) => {
        if (!isStatusState(state) || state.providerId !== providerId) {
          return undefined;
        }

        const promptStatus = { ...state.status };
        if (excludeSubscribeStep) {
          delete promptStatus.prompt_for_subscribe;
        }

        return {
          promptStatus,
          percentComplete: calculatePercentComplete(promptStatus),
        };
      },
    ),
};

export const ProviderOnboarding = {
  name: 'providerOnboarding',

  state: {} as State,

  reducers: {
    onClear(): State {
      return {};
    },

    onFetchStart(
      state: State,
      { providerId }: Pick<StatusState, 'providerId'>,
    ): StatusState {
      return {
        ...(isStatusState(state) && state.providerId === providerId
          ? state
          : { providerId }),
        isFetching: true,
      };
    },

    onFetchSuccess(
      state: State,
      { providerId, status }: Required<Pick<StatusState, 'providerId' | 'status'>>,
    ): State {
      return {
        ...state,
        ...(isStatusState(state) && state.providerId === providerId
          ? {
            isFetching: false,
            status,
          }
          : {}),
      };
    },

    onFetchFail(
      state: State,
      { providerId }: Pick<StatusState, 'providerId'>,
    ): State {
      return {
        ...state,
        ...(isStatusState(state) && state.providerId === providerId
          ? {
            isFetching: false,
          }
          : {}),
      };
    },
  },

  effects: (dispatch: IRootDispatch) => ({
    async fetch(
      providerId: StatusState['providerId'],
      { providerOnboarding }: RootState,
    ) {
      const {
        providerId: currentProviderId,
        isFetching,
      } = isStatusState(providerOnboarding)
        ? providerOnboarding
        : {
          providerId: undefined,
          isFetching: undefined,
        };

      if (providerId === currentProviderId && isFetching) {
        return;
      }

      await dispatch.providerOnboarding.onFetchStart({ providerId });
      try {
        let status = await fetchProviderOnboardingPromptStatus(providerId);
        status = await transformPromptStatus(status);
        await dispatch.providerOnboarding.onFetchSuccess({ providerId, status });
      } catch {
        await dispatch.providerOnboarding.onFetchFail({ providerId });
      }
    },
  }),

  selectors: () => ({
    status: () => selectors.status,
  }),
};

export type Effects = {
  fetch: (providerId: StatusState['providerId']) => Promise<void>;
};
